import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProfissionalAusenciaService } from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { DeviceService } from '@nomoh-monorepo/shared-common-ui';
import { IProfissional, IProfissionalDataAusente } from '@nomoh-monorepo/shared-types';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AusenciaCriarEditarComponent } from './ausencia-criar-editar/ausencia-criar-editar.component';

@Component({
  selector: 'lib-profissional-ausencias',
  standalone: true,
  templateUrl: './profissional-ausencias.component.html',
  styleUrls: ['./profissional-ausencias.component.scss'],
  imports: [
    ButtonModule,
    ConfirmDialogModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    TooltipModule,
    DialogModule,
    AusenciaCriarEditarComponent,
    TranslateModule,
  ],
  providers: [ConfirmationService],
})
export class ProfissionalAusenciasComponent implements OnChanges {
  //#region Inputs e Outputs
  @Input() profissional!: IProfissional;

  @ViewChild(AusenciaCriarEditarComponent)
  ausenciaCriarEditarRef?: AusenciaCriarEditarComponent;

  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerReset = new EventEmitter<void>();
  //#endregion

  //#region Injeção de Dependências
  private ausenciaService = inject(ProfissionalAusenciaService);
  private notificacaoService = inject(NotificacaoService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);
  deviceService = inject(DeviceService);
  //#endregion

  //#region Signals
  $paginatorDropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );

  $ausencias = signal<IProfissionalDataAusente[]>([]);
  $ausenciasFiltradas = computed(() => {
    const termo = this.pesquisa().toLowerCase().trim();
    if (!termo) return this.$ausencias();
    return this.$ausencias().filter((a) =>
      a.motivo?.toLowerCase().includes(termo)
    );
  });
  $loading = signal(false);
  $criandoAusencia = signal(false);
  $ausenciaSelecionada = signal<IProfissionalDataAusente | null>(null);
  pesquisa = signal('');
  rows = signal(10);
  //#endregion

  //#region Lifecycle
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['profissional']?.currentValue) {
      await this.carregarAusencias();
    }
  }
  //#endregion

  //#region Métodos
  verDetalhes(ausencia: IProfissionalDataAusente): void {
    this.$ausenciaSelecionada.set(ausencia);
  }

  fecharDetalhes(): void {
    this.$ausenciaSelecionada.set(null);
  }

  async carregarAusencias(): Promise<void> {
    if (!this.profissional?.id) {
      return;
    }

    this.$loading.set(true);
    try {
      const ausencias = await this.ausenciaService.listar(this.profissional.id);
      this.$ausencias.set(ausencias);
    } catch {
      this.notificacaoService.notificacaoError(
        this.translateService.instant('BARDO_PROFISSIONAIS.AUSENCIAS_TAB.ERRO_CARREGAR')
      );
    } finally {
      this.$loading.set(false);
    }
  }

  iniciarCriacao(): void {
    this.$criandoAusencia.set(true);
  }

  cancelarCriacao(): void {
    this.$criandoAusencia.set(false);
    this.footerReset.emit();
  }

  onFooterTemplate(template: TemplateRef<unknown>): void {
    this.footerTemplateAction.emit(template);
  }

  async onAusenciaSalva(): Promise<void> {
    this.cancelarCriacao();
    await this.carregarAusencias();
  }

  excluirAusencia(ausencia: IProfissionalDataAusente): void {
    if (!this.profissional?.id) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translateService.instant('BARDO_SERVICOS.CONFIRMAR_EXCLUSAO_SERVICO', { termo: 'ausência' }),
      header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
      rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        if (!this.profissional?.id) return;
        try {
          await this.ausenciaService.deletar(
            this.profissional.id,
            ausencia.id as string
          );
          this.$ausencias.update((curr) =>
            curr.filter((a) => a.id !== ausencia.id)
          );
          this.notificacaoService.notificacaoSuccess(
            this.translateService.instant('BARDO_PROFISSIONAIS.AUSENCIAS_TAB.SUCESSO_EXCLUIR')
          );
        } catch {
          this.notificacaoService.notificacaoError(
            this.translateService.instant('BARDO_PROFISSIONAIS.AUSENCIAS_TAB.ERRO_REMOVER')
          );
        }
      },
    });
  }

  formatarDataHora(valor: Date | string | null | undefined): string {
    if (!valor) return '--';
    const d = new Date(valor);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const hora = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dia}/${mes} ${hora}:${min}`;
  }
  //#endregion
}
